import React, { useCallback, useEffect, useRef, useState } from 'react';

import AccessibilityAnnouncerContext from '@lib/context/AccessibilityAnnouncerContext';
import { useAccessibilityAnnouncer } from '@lib/context/AccessibilityAnnouncerContext';

interface Props {
    children: React.ReactNode;
}

const visuallyHiddenStyle: React.CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0
};

const AccessibilityAnnouncerProvider: React.FC<Props> = ({ children }) => {
    const [message, setMessage] = useState('');
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const announce = useCallback((msg: string) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setMessage(msg);
        timerRef.current = setTimeout(() => setMessage(''), 5000);
    }, []);

    return (
        <AccessibilityAnnouncerContext.Provider value={{ announce, message }}>
            {children}
        </AccessibilityAnnouncerContext.Provider>
    );
};

export const AccessibilityAnnouncerOutlet: React.FC = () => {
    const { message } = useAccessibilityAnnouncer();
    const [delayedMessage, setDelayedMessage] = useState('');

    useEffect(() => {
        if (!message) {
            setDelayedMessage('');
            return;
        }

        const timer = setTimeout(() => {
            setDelayedMessage(message);
        }, 50);

        return () => {
            clearTimeout(timer);
        };
    }, [message]);

    return (
        <div aria-live="assertive" aria-atomic="true" style={visuallyHiddenStyle}>
            {delayedMessage}
        </div>
    );
};

export default AccessibilityAnnouncerProvider;
